import { BigNumber } from 'bignumber.js';
import { ApiResult } from '../../../../api/types';
import getDisplayApprovalProgress from '../../../../common/approval/helpers/getDisplayApprovalProgress';
import { EnumOperationType } from '../../../../enum/EnumOperationType';
import { EnumTransactionStatus } from '../../../../enum/EnumTransactionStatus';
import { useTranslation } from '../../../../hooks';
import useChains from '../../../../hooks/useChains';
import { toDisplayTime } from '../../../../utils';
import { GetListRes } from '../types/GetListRes';
import { TableColumnKey, TableRow } from '../types/Table';
import useMerchantList from '../../../../hooks/useMerchantList';

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type ConvertToTableRowsType = (apiList: ApiResult<GetListRes>) => TableRow[];

const formatDate = (date: Date | string | number) => {
  if (!date) return '';

  return toDisplayTime(date as any);
};

const timeStampToISOString = (timestamp: string) => {
  const timeInMs = BigNumber(timestamp).multipliedBy(1000).toNumber();
  return new Date(timeInMs).toISOString();
};

const useTableRows = (props: UseTableRowsProps) => {
  const { apiList } = props;
  const { t, te, translate } = useTranslation('transactionRequest');
  const { chainList, getChainInfo } = useChains();
  const { getMerchantInfo } = useMerchantList();

  const convertToTableRows: ConvertToTableRowsType = (apiList) => {
    if (!apiList || !apiList?.rows) return [];

    const tableRows = apiList?.rows.map((row): TableRow => {
      const chainInfo = getChainInfo(row.chainType, row.chainId);
      const requestTime = timeStampToISOString(row.requestTime);

      const merchantInfo = getMerchantInfo(row.merchantId);

      const merchantName = merchantInfo ? merchantInfo.name : '';

      return {
        [TableColumnKey.ID]: row.id,
        [TableColumnKey.MERCHANT_NAME]: merchantName,
        [TableColumnKey.BATCH_ID]: row.batchId,
        [TableColumnKey.OPERATION_TYPE]: translate(
          `EnumOperationType.${EnumOperationType[row.operationType]}`
        ),
        [TableColumnKey.CHAIN_NAME]: chainInfo?.displayName || '',
        [TableColumnKey.APPROVAL_PROGRESS]: getDisplayApprovalProgress({
          approvalsCount: row.approvalCompleted,
          approvalsRequired: row.approvalRequired,
          // applicationStatus: row.status,
          // approvedStatus: EnumTransactionStatus.Approved,
          translateFunc: te,
        }),
        [TableColumnKey.TOTAL_OPERATION]: row.totalOperation,
        [TableColumnKey.REQUEST_TIME]: formatDate(requestTime),
        [TableColumnKey.STATUS]: translate(
          `EnumTransactionStatus.${EnumTransactionStatus[row.status]}`
        ),
        [TableColumnKey.CREATED_BY]: row.createdBy,
        [TableColumnKey.CREATED_DATE]: formatDate(row.createdDate),
        [TableColumnKey.LAST_MODIFIED_BY]: row.lastModifiedBy,
        [TableColumnKey.LAST_MODIFIED_DATE]: formatDate(row.lastModifiedDate),
        [TableColumnKey.TRANSACTION_OPERATIONS]: row.transactionOperations?.length || 0,
        rawData: row,
      };
    });

    return tableRows;
  };

  const rows = convertToTableRows(apiList);

  return { rows, totalCounts: apiList.count, convertToTableRows };
};

export default useTableRows;
